/**
 * Expand-section — Phase 4b structural block (wiki feature plan §4b).
 *
 * Collapsible section with a per-block `summary` label and a `block+`
 * body. Click the chevron OR the summary row to toggle; collapsed/open
 * state persists per-reader via `useExpandState` (localStorage,
 * `wiki:expand:{docId}:{blockId}`).
 *
 * Schema shape:
 *   - `group: 'block'`           — sits anywhere a block can.
 *   - `content: 'block+'`        — holds one or more block children
 *                                  (paragraphs, lists, headings, hr,
 *                                  even panels — but NOT another expand;
 *                                  the slash-command run() guards that
 *                                  case, same shape as the panel-in-panel
 *                                  guard).
 *   - `defining: true`           — backspace at the start of the first
 *                                  body child unwraps the expand rather
 *                                  than dissolving it.
 *   - `isolating: true`          — selection ops respect the boundary.
 *   - `selectable: true`         — Escape from a body child surfaces a
 *                                  NodeSelection on the container.
 *   - `draggable: true`          — the leading drag handle gives a
 *                                  reorder affordance, same convention
 *                                  as the panel set.
 *
 * Attributes:
 *   - `summary: string` — author-controlled label rendered next to the
 *     chevron. Default `'Click to expand'` (a sentinel placeholder; the
 *     i18n key resolves to the localized form on render).
 *   - `blockId: string` — opaque, per-instance id. The slash command's
 *     `run()` mints a fresh `crypto.randomUUID()` at insert time and
 *     attaches it. **NEVER default to `crypto.randomUUID()` in
 *     `addAttributes`** — defaults are evaluated once per schema and
 *     every expand would share the same id, collapsing the localStorage
 *     keying. The default here is an empty string; the NodeView's
 *     `useExpandState` call gracefully handles a missing id by falling
 *     back to a one-shot in-memory state.
 *
 * Round-trip:
 *   - parseHTML matches `details[data-block-type="expand-section"]`.
 *   - renderHTML emits the same shape with `data-summary` + `data-block-id`
 *     attributes. The `<details>` tag is the canonical fallback render
 *     for an HTML reader that doesn't parse the JSON — drop a saved doc
 *     into a vanilla browser and the section is still expand/collapsible.
 */

import { Node, type ReactNodeViewProps, ReactNodeViewRenderer } from '@tiptap/react'
import type { ComponentType } from 'react'

import { exitIsolatingBlockDown } from '@/editor/lib/exit-isolating-block-down'

export interface BuildExpandSectionExtensionOptions {
  /**
   * React NodeView. The factory accepts it as a param so the unit test
   * suite can stub a no-op view if needed.
   */
  view: ComponentType<ReactNodeViewProps<HTMLElement>>
}

export function buildExpandSectionExtension({ view }: BuildExpandSectionExtensionOptions) {
  return Node.create({
    name: 'expandSection',
    group: 'block',
    content: 'block+',
    defining: true,
    isolating: true,
    selectable: true,
    draggable: true,

    addAttributes() {
      return {
        summary: {
          default: '',
          parseHTML: (el: HTMLElement) => el.getAttribute('data-summary') ?? '',
          renderHTML: (attrs: { summary?: string }) => ({
            'data-summary': attrs.summary ?? '',
          }),
        },
        blockId: {
          // Empty string default — the slash-command run() mints a
          // fresh `crypto.randomUUID()` on insert. Generating in the
          // default would give every expand the same id (defaults run
          // once per schema, not per-instance).
          default: '',
          parseHTML: (el: HTMLElement) => el.getAttribute('data-block-id') ?? '',
          renderHTML: (attrs: { blockId?: string }) => ({
            'data-block-id': attrs.blockId ?? '',
          }),
        },
      }
    },

    parseHTML() {
      return [{ tag: 'details[data-block-type="expand-section"]' }]
    },

    renderHTML({ HTMLAttributes }) {
      return [
        'details',
        { ...HTMLAttributes, 'data-block-type': 'expand-section' },
        // Body 0-hole — ProseMirror inlines the editable children here.
        0,
      ]
    },

    addNodeView() {
      return ReactNodeViewRenderer(view)
    },

    addKeyboardShortcuts() {
      // ArrowDown at end of body exits into a fresh paragraph below
      // (creating one if needed) — same gap-cursor avoidance as the
      // panels. See `exit-isolating-block-down.ts` for the full
      // rationale.
      const exit = exitIsolatingBlockDown('expandSection')
      return {
        ArrowDown: ({ editor }) => editor.commands.command(exit),
        'Mod-Enter': ({ editor }) => editor.commands.command(exit),
      }
    },
  })
}
